import type { ReactNode } from "react";
import isFunction from "lodash/isFunction";
import type { LinkProps } from "react-router-dom";
import type { IconType } from "@circle-react-shared/Icon";
import { Icon } from "@circle-react-shared/Icon";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { Typography } from "@circle-react-uikit/Typography";
import { Item } from "./Item";
import { ItemLink } from "./ItemLink";
import { Separator } from "./Separator";

export interface RegularOption {
  badge?: string;
  badgeVariant?: string;
  className?: string;
  color?: string;
  dataTestId?: string;
  description?: string;
  hide?: boolean;
  icon?: IconType;
  iconClassName?: string;
  iconSize?: number;
  isActive?: boolean;
  isDisabled?: boolean;
  label: ReactNode;
  onClick?: (() => void) | (() => Promise<void>);
  pathTo?: LinkProps["to"];
  value: string;
}

export interface SeparatorOption {
  value: "separator";
}

export type Option = RegularOption | SeparatorOption;

export const isSeparatorOption = (option: Option): option is SeparatorOption =>
  option.value === "separator";

export interface OptionProps {
  isActive?: boolean;
  isDark?: boolean;
  labelWeight?: string;
  linkType?: "button" | "submit" | "reset";
  onChange?: (value: string) => void;
  option: Option;
}

export const Option = ({
  option,
  onChange,
  linkType = "submit",
  labelWeight: labelWeightProp,
  isDark,
  isActive: isActiveProp,
}: OptionProps) => {
  const labelWeight = labelWeightProp ?? "medium";

  if (isSeparatorOption(option)) {
    return <Separator />;
  }

  if (option?.hide) return null;

  if (isDark) {
    option.color = "text-feature-light group-hover:text-dark";
  }

  return (
    <Item key={option.value}>
      {({ active }: { active: boolean }) => (
        <ItemLink
          active={isActiveProp !== undefined ? isActiveProp : active}
          to={option?.pathTo}
          onClick={() => {
            isFunction(onChange) && onChange(option.value);
            isFunction(option?.onClick) && option.onClick();
          }}
          linkType={linkType}
          isDark={isDark}
          isDisabled={option?.isDisabled}
          className={option?.className || ""}
        >
          <Dropdown.IconContainer>
            {option.icon && (
              <span className="size-5">
                <Icon
                  className={option.iconClassName}
                  type={option.icon}
                  size={option.iconSize ?? 16}
                />
              </span>
            )}
            <Typography.LabelXsPlus weight={labelWeight} color={option.color}>
              {option.label}
            </Typography.LabelXsPlus>
          </Dropdown.IconContainer>
          {option.description && (
            <div className="py-0.5">{option.description}</div>
          )}
        </ItemLink>
      )}
    </Item>
  );
};
